import React from 'react';
import styled from '@emotion/styled';
import { AigleButton } from './AigleButton';
import { aigleColor } from '@styles/colorStyleGuide';
import { Icon } from '@iconify/react/dist/iconify.js';


// 스타일 컴포넌트
const Container = styled.div`
  padding: 40px;
  background-color: ${aigleColor.grey.white};
  min-height: 100vh;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${aigleColor.grey.black};
  margin-bottom: 40px;
`;

const Section = styled.div`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${aigleColor.grey.black};
  margin-bottom: 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 150px repeat(2, 1fr);
  gap: 16px;
  align-items: center;
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${aigleColor.grey.darkest};
`;

const HeaderCell = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${aigleColor.grey.black};
  text-align: center;
`;

const ButtonCell = styled.div`
  display: flex;
  justify-content: center;
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${aigleColor.grey.light};
  margin: 32px 0;
`;

/**
 * ButtonStyleGuideExample
 * 
 * 모든 버튼 스타일을 한눈에 볼 수 있는 가이드 페이지
 */
export const ButtonStyleGuideExample: React.FC = () => {
  return (
    <Container>
      <Title>Button Style Guide</Title>

      {/* Normal Buttons */}
      <Section>
        <Grid>
          <div></div>
          <HeaderCell>Contained</HeaderCell>
          <HeaderCell>Outlined</HeaderCell>

          <Label>Primary</Label>
          <ButtonCell>
            <AigleButton variant="contained" color="primary">
              Button
            </AigleButton>
          </ButtonCell>
          <ButtonCell>
            <AigleButton variant="outlined" color="primary">
              Button
            </AigleButton>
          </ButtonCell>

          <Label>Secondary</Label>
          <ButtonCell>
            <AigleButton variant="contained" color="secondary">
              Button
            </AigleButton>
          </ButtonCell>
          <ButtonCell>
            <AigleButton variant="outlined" color="secondary">
              Button
            </AigleButton>
          </ButtonCell>

          <Label>Default</Label>
          <ButtonCell>
            <AigleButton variant="contained" color="default">
              Button
            </AigleButton>
          </ButtonCell>
          <ButtonCell>
            <AigleButton variant="outlined" color="default">
              Button
            </AigleButton>
          </ButtonCell>

          <Label>Disabled</Label>
          <ButtonCell>
            <AigleButton variant="contained" color="primary" disabled>
              Button
            </AigleButton>
          </ButtonCell>
          <ButtonCell>
            <AigleButton variant="outlined" color="primary" disabled>
              Button
            </AigleButton>
          </ButtonCell>
        </Grid>
      </Section>

      <Divider />

      {/* Icon Buttons - icon */}
      <Section>
        <Grid>
          <div></div>
          <HeaderCell>Contained</HeaderCell>
          <HeaderCell>Outlined</HeaderCell>

          <Label>Primary</Label>
          <ButtonCell>
            <AigleButton variant="contained" color="primary" 
            startIcon={<Icon icon="material-symbols-light:refresh-rounded"/>}>
              Button
            </AigleButton>
          </ButtonCell>
          <ButtonCell>
            <AigleButton variant="outlined" color="primary" 
            startIcon={<Icon icon="material-symbols-light:refresh-rounded"/>}>
              Button
            </AigleButton>
          </ButtonCell>

          <Label>Secondary</Label>
          <ButtonCell>
            <AigleButton variant="contained" color="secondary" 
            startIcon={<Icon icon="material-symbols-light:refresh-rounded"/>}>
              Button
            </AigleButton>
          </ButtonCell>
          <ButtonCell>
            <AigleButton variant="outlined" color="secondary" 
            startIcon={<Icon icon="material-symbols-light:refresh-rounded"/>}>
              Button
            </AigleButton>
          </ButtonCell>

          <Label>Default</Label>
          <ButtonCell>
            <AigleButton variant="contained" color="default" 
            startIcon={<Icon icon="material-symbols-light:refresh-rounded"/>}>
              Button
            </AigleButton>
          </ButtonCell>
          <ButtonCell>
            <AigleButton variant="outlined" color="default" 
            startIcon={<Icon icon="material-symbols-light:refresh-rounded"/>}>
              Button
            </AigleButton>
          </ButtonCell>

          <Label>Disabled</Label>
          <ButtonCell>
            <AigleButton variant="contained" color="primary" 
            startIcon={<Icon icon="material-symbols-light:refresh-rounded"/>} disabled>
              Button
            </AigleButton>
          </ButtonCell>
          <ButtonCell>
            <AigleButton variant="outlined" color="primary" 
            startIcon={<Icon icon="material-symbols-light:refresh-rounded"/>} disabled>
              Button
            </AigleButton>
          </ButtonCell>
        </Grid>
      </Section>

      <Divider />

      {/* Transparent Buttons */}
      <Section>
        <SectionTitle>Transparent Buttons</SectionTitle>
        <AigleButton variant="transparent">
          Button
        </AigleButton>
      </Section>

      <Divider />

      {/* 추가 예제: 다양한 사이즈 */}
      <Section>
        <SectionTitle>Button Sizes</SectionTitle>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'center' }}>
          <AigleButton variant="contained" color="primary" size="small">
            Small
          </AigleButton>
          <AigleButton variant="contained" color="primary" size="medium">
            Medium
          </AigleButton>
          <AigleButton variant="contained" color="primary" size="large">
            Large
          </AigleButton>
        </div>
      </Section>

      {/* 추가 예제: Full Width */}
      <Section>
        <SectionTitle>Full Width Button</SectionTitle>
        <AigleButton variant="contained" color="primary" fullWidth>
          Full Width Button
        </AigleButton>
      </Section>

      {/* 추가 예제: Icon Only */}
      <Section>
        <SectionTitle>Icon Only Buttons</SectionTitle>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'center' }}>
          <AigleButton iconOnly color="primary">
            <Icon icon="material-symbols-light:refresh-rounded"/>
          </AigleButton>
          <AigleButton iconOnly color="default">
            <Icon icon="material-symbols-light:refresh-rounded"/>
          </AigleButton>
        </div>
      </Section>
    </Container>
  );
};

export default ButtonStyleGuideExample;

